import { DateTime } from 'luxon';
import { Predicate, PredicateOperator } from 'app/enums';
import { SelectedFilter } from './filter-model';

/**
 * Converts a SelectedFilter object into an array of Predicate objects for API consumption.
 * This utility function can be used by any component that needs to convert filters to predicates.
 * Merges the 5 measurement filter arrays (firstAmplitude, lastAmplitude, firstResult, lastResult, ptcUse)
 * into a single measurementDataFilters array for the server.
 *
 * @param selectedFilter - The filter object to convert
 * @returns Array of predicates ready for API requests
 */
export function convertFilterToPredicates(selectedFilter: SelectedFilter): Predicate<boolean | string>[] {
    const predicates: Predicate<boolean | string>[] = [];

    // Merge all measurement filter categories into one array
    const measurementFilters = [
        ...(selectedFilter.firstAmplitude || []),
        ...(selectedFilter.lastAmplitude || []),
        ...(selectedFilter.firstResult || []),
        ...(selectedFilter.lastResult || []),
        ...(selectedFilter.ptcUse || [])
    ];

    Object.entries(selectedFilter).forEach(([key, value]) => {
        // Skip individual measurement filter keys, handle them separately
        if (['firstAmplitude', 'lastAmplitude', 'firstResult', 'lastResult', 'ptcUse'].includes(key)) {
            return;
        }

        if ((typeof value === 'string' || typeof value === 'number') && !!value) {
            predicates.push({
                propertyName: key,
                operator: PredicateOperator.Contains,
                value: value.toString()
            });
        } else if (typeof value === 'boolean' && !!value) {
            predicates.push({
                propertyName: key,
                operator: PredicateOperator.Equals,
                value: value
            });
        } else if (Array.isArray(value)) {
            if (value.length > 0) {
                predicates.push({
                    propertyName: key,
                    operator: PredicateOperator.Contains,
                    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                    value: `${value}`
                });
            }
        } else {
            if (`${key}`.endsWith('DateRange')) {
                const dateRange = value as { from: DateTime | null; to: DateTime | null };
                if (dateRange.from) {
                    predicates.push({
                        propertyName: `${key}.from`,
                        operator: PredicateOperator.GreaterThanOrEqual,
                        value: dateRange.from.toFormat('yyyy-MM-dd')
                    });
                }
                const uploadDateRangeEndsToday =
                    key === 'uploadDateRange' && dateRange.to?.hasSame(DateTime.local(), 'day');
                if (dateRange.to && !uploadDateRangeEndsToday) {
                    predicates.push({
                        propertyName: `${key}.to`,
                        operator: PredicateOperator.LessThanOrEqual,
                        value: dateRange.to.plus({ day: 1 }).toFormat('yyyy-MM-dd')
                    });
                }
            }
        }
    });

    // Add merged measurement filters as a single predicate
    if (measurementFilters.length > 0) {
        predicates.push({
            propertyName: 'measurementDataFilters',
            operator: PredicateOperator.Contains,
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            value: `${measurementFilters}`
        });
    }

    return predicates;
}
